import { useEffect, useState, useMemo, Dispatch, MouseEvent, SetStateAction, type ReactNode } from 'react';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import Collapse from '@mui/material/Collapse';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project-imports
import NavItem from './NavItem';
import Dot from 'components/@extended/Dot';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import SimpleBar from 'components/third-party/SimpleBar';

import { useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import useMenuCollapse from 'hooks/useMenuCollapse';

// third-party
import { FormattedMessage } from 'react-intl';

// assets
import { ArrowDown2, ArrowUp2, ArrowRight2, Copy } from 'iconsax-reactjs';

// types
import { NavItemType } from 'types/menu';

type VirtualElement = {
  getBoundingClientRect: () => DOMRect;
  contextElement?: Element;
};

type AnchorElState = VirtualElement | (() => VirtualElement) | null | undefined;

function collapseDrawerIndentPadding(level: number, drawerOpen: boolean): number {
  if (level === 2) return 3.25;
  if (!drawerOpen) return 1.5;
  if (level <= 3) return (level * 20) / 8;
  return (level * 20 + (level - 3) * 10) / 8;
}

function mapNavMenuChild(
  item: NavItemType,
  ctx: {
    level: number;
    parentId: string;
    setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
    setSelectedLevel: Dispatch<SetStateAction<number>>;
    selectedLevel: number;
    selectedItems: string | undefined;
  }
) {
  switch (item.type) {
    case 'collapse':
      return (
        <NavCollapse
          key={item.id}
          setSelectedItems={ctx.setSelectedItems}
          setSelectedLevel={ctx.setSelectedLevel}
          selectedLevel={ctx.selectedLevel}
          selectedItems={ctx.selectedItems}
          menu={item}
          level={ctx.level + 1}
          parentId={ctx.parentId}
        />
      );
    case 'item':
      return <NavItem key={item.id} item={item} level={ctx.level + 1} />;
    default:
      return (
        <Typography key={item.id} variant="h6" color="error" align="center">
          Fix - Collapse or Item
        </Typography>
      );
  }
}

function NavCollapseChevronTrail({
  miniMenuOpened,
  open,
  arrowStyle
}: Readonly<{ miniMenuOpened: boolean; open: boolean; arrowStyle: { size: number; style: { marginLeft: number } } }>) {
  if (miniMenuOpened) return <ArrowRight2 {...arrowStyle} />;
  if (open) return <ArrowUp2 {...arrowStyle} />;
  return <ArrowDown2 {...arrowStyle} />;
}

function verticalCollapseMainListItemSx(theme: Theme, level: number, drawerOpen: boolean): SystemStyleObject<Theme> {
  return {
    pl: collapseDrawerIndentPadding(level, drawerOpen),
    py: !drawerOpen && level === 1 ? 1.25 : 1,
    ...(drawerOpen &&
      level === 1 && {
        mx: 1.25,
        my: 0.5,
        borderRadius: 1,
        '&:hover': { bgcolor: 'secondary.200', ...theme.applyStyles('dark', { bgcolor: 'divider' }) }
      }),
    ...(!drawerOpen && {
      px: 2.75,
      '&:hover': { bgcolor: 'transparent' },
      '&.Mui-selected': {
        '&:hover': { bgcolor: 'transparent' },
        bgcolor: 'transparent'
      }
    })
  };
}

function verticalCollapseMenuIconSx(
  theme: Theme,
  lightTextColor: string,
  darkTextColor: string,
  drawerOpen: boolean,
  iconEmphasis: boolean
): SystemStyleObject<Theme> {
  return {
    minWidth: 38,
    color: lightTextColor,
    ...theme.applyStyles('dark', { color: darkTextColor }),
    ...(!drawerOpen && {
      borderRadius: 1,
      width: 46,
      height: 46,
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': { bgcolor: 'secondary.200', ...theme.applyStyles('dark', { bgcolor: 'divider' }) },
      ...(iconEmphasis && {
        bgcolor: 'primary.lighter',
        '&:hover': { bgcolor: 'primary.lighter' },
        ...theme.applyStyles('dark', { bgcolor: 'divider', '&:hover': { bgcolor: 'divider' } })
      })
    })
  };
}

function verticalCollapseTitleTypographySx(
  theme: Theme,
  titleEmphasis: boolean,
  lightTextColor: string,
  darkTextColor: string
): SystemStyleObject<Theme> {
  return {
    fontWeight: titleEmphasis ? 500 : 400,
    color: lightTextColor,
    ...theme.applyStyles('dark', { color: darkTextColor })
  };
}

function verticalCollapseChevronIconButtonSx(
  theme: Theme,
  open: boolean,
  lightTextColor: string,
  darkTextColor: string
): SystemStyleObject<Theme> {
  return {
    width: 20,
    height: 20,
    mr: '-5px !important',
    p: 0.25,
    borderColor: open ? 'primary.light' : 'secondary.light',
    '&:hover': { borderColor: open ? 'primary.main' : 'secondary.main' },
    color: lightTextColor,
    ...theme.applyStyles('dark', { color: darkTextColor })
  };
}

function collapseMiniPopperPaperSx(theme: Theme, compact?: boolean): SystemStyleObject<Theme> {
  return {
    overflow: 'hidden',
    ...(compact ? { py: 0.5 } : {}),
    boxShadow: theme.customShadows.z1,
    border: '1px solid ',
    borderColor: 'divider',
    backgroundImage: 'none'
  };
}

function NavCollapseDrawerExpansion(props: Readonly<{
  menu: NavItemType;
  drawerOpen: boolean;
  open: boolean;
  openCollapse: boolean;
  anchorElCollapse: HTMLElement | null;
  handleCloseCollapse: () => void;
  theme: Theme;
  level: number;
  navCollapse: ReactNode;
}>) {
  const { menu, drawerOpen, open, openCollapse, anchorElCollapse, handleCloseCollapse, theme, level, navCollapse } = props;
  if (!drawerOpen) return null;
  if (!menu?.isDropdown) {
    return (
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List sx={{ py: 0, px: level <= 1 ? 1.5 : 0 }}>{navCollapse}</List>
      </Collapse>
    );
  }
  return (
    <Menu
      id={`${menu.id}-menu`}
      aria-labelledby={`${menu.id}-button`}
      anchorEl={anchorElCollapse}
      open={openCollapse}
      onClose={handleCloseCollapse}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      slotProps={{ paper: { sx: { boxShadow: theme.shadows[2] } } }}
    >
      {navCollapse}
    </Menu>
  );
}

function horizontalMiniCollapseListItemSx(level: number): SystemStyleObject<Theme> {
  return {
    zIndex: 121,
    borderRadius: level >= 1 ? 0 : 1,
    '&.Mui-selected': {
      '&:hover': {
        bgcolor: 'transparent'
      },
      bgcolor: 'transparent'
    }
  };
}

// mini-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 180,
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 38,
    left: -5,
    width: 10,
    height: 10,
    backgroundColor: theme.palette.background.paper,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}));

// ==============================|| NAVIGATION - COLLAPSE ||============================== //

function applyNavCollapseSelectionSync(p: {
  selected: string | null | undefined;
  selectedItems: string | undefined;
  level: number;
  selectedLevel: number;
  miniMenuOpened: boolean;
  drawerOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelected: React.Dispatch<React.SetStateAction<string | null | undefined>>;
}): void {
  if (p.selected === p.selectedItems) {
    if (p.level === 1) {
      p.setOpen(true);
    }
    return;
  }
  if (p.level !== p.selectedLevel) {
    return;
  }
  p.setOpen(false);
  if ((!p.miniMenuOpened && !p.drawerOpen && !p.selected) || p.drawerOpen) {
    p.setSelected(null);
  }
}

function runNavCollapsePrimaryClick(p: {
  event: React.MouseEvent<HTMLElement> | undefined;
  isRedirect: boolean;
  drawerOpen: boolean;
  open: boolean;
  selected: string | null | undefined;
  menu: NavItemType;
  level: number;
  navigation: NavigateFunction;
  setAnchorEl: React.Dispatch<React.SetStateAction<AnchorElState>>;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelected: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
}): void {
  p.setAnchorEl(null);
  p.setSelectedLevel(p.level);
  if (!p.drawerOpen) {
    p.setAnchorEl(p.event?.currentTarget);
    return;
  }
  p.setOpen(!p.open);
  p.setSelected(!p.selected ? p.menu.id : null);
  p.setSelectedItems(!p.selected ? p.menu.id : '');
  if (p.menu.url && p.isRedirect) {
    p.navigation(`${p.menu.url}`);
  }
}

function runNavCollapseIconLink(p: {
  drawerOpen: boolean;
  menu: NavItemType;
  menuOrientation: MenuOrientation;
  navigation: NavigateFunction;
  setSelected: React.Dispatch<React.SetStateAction<string | null | undefined>>;
}): void {
  if (p.drawerOpen) return;
  if (p.menu.url) {
    p.navigation(`${p.menu.url}`);
  }
  if (p.menuOrientation === MenuOrientation.VERTICAL) {
    p.setSelected(p.menu.id);
  }
}

function runNavCollapseClose(p: {
  miniMenuOpened: boolean;
  menu: NavItemType;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelected: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  setAnchorEl: React.Dispatch<React.SetStateAction<AnchorElState>>;
}): void {
  p.setOpen(false);
  if (!p.miniMenuOpened && !p.menu.url) {
    p.setSelected(null);
  }
  p.setAnchorEl(null);
}

function NavCollapseVerticalStack(props: Readonly<{
  menu: NavItemType;
  level: number;
  drawerOpen: boolean;
  isSelected: boolean;
  anchorEl: AnchorElState;
  open: boolean;
  miniMenuOpened: boolean;
  openCollapse: boolean;
  anchorElCollapse: HTMLElement | null;
  navCollapse: ReactNode;
  theme: Theme;
  handleClick: (event: React.MouseEvent<HTMLElement> | undefined, isRedirect: boolean) => void;
  handleMiniClose: () => void;
  handleClickCollapse: (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  handleCloseCollapse: () => void;
  handlerIconLink: () => void;
  handleClose: () => void;
  menuIcon: ReactNode;
  lightTextColor: string;
  darkTextColor: string;
  arrowStyle: { size: number; style: { marginLeft: number } };
}>) {
  const {
    menu,
    level,
    drawerOpen,
    isSelected,
    anchorEl,
    open,
    miniMenuOpened,
    openCollapse,
    anchorElCollapse,
    navCollapse,
    theme,
    handleClick,
    handleMiniClose,
    handleClickCollapse,
    handleCloseCollapse,
    handlerIconLink,
    handleClose,
    menuIcon,
    lightTextColor,
    darkTextColor,
    arrowStyle
  } = props;

  return (
    <>
      <ListItemButton
        component="div"
        id={`${menu.id}-button`}
        selected={isSelected}
        {...(!drawerOpen && {
          onMouseEnter: (e: React.MouseEvent<HTMLElement>) => handleClick(e, true),
          onMouseLeave: handleMiniClose
        })}
        onClick={(e: React.MouseEvent<HTMLElement>) => handleClick(e, true)}
        sx={(themeArg) => verticalCollapseMainListItemSx(themeArg, level, drawerOpen)}
        {...((drawerOpen &&
          menu.isDropdown && {
            'aria-controls': openCollapse ? `${menu.id}-menu` : undefined,
            'aria-haspopup': true,
            'aria-expanded': openCollapse ? 'true' : undefined,
            onClick: handleClickCollapse
          }) as Record<string, unknown>)}
      >
        {menuIcon && (
          <ListItemIcon
            onClick={handlerIconLink}
            sx={(themeArg) =>
              verticalCollapseMenuIconSx(themeArg, lightTextColor, darkTextColor, drawerOpen, Boolean(isSelected || anchorEl))
            }
          >
            {menuIcon}
          </ListItemIcon>
        )}

        {!menuIcon && drawerOpen && (
          <ListItemIcon sx={{ minWidth: 30 }}>
            <Dot size={isSelected || anchorEl ? 6 : 5} color={isSelected || anchorEl ? 'primary' : 'secondary'} />
          </ListItemIcon>
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={(themeArg) =>
                  verticalCollapseTitleTypographySx(themeArg, Boolean(isSelected || anchorEl), lightTextColor, darkTextColor)
                }
              >
                <FormattedMessage id={menu.title} />
              </Typography>
            }
            secondary={
              menu.caption && (
                <Typography variant="caption" color="secondary">
                  <FormattedMessage id={menu.caption} />
                </Typography>
              )
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) &&
          (menu?.url ? (
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                handleClick(event, false);
              }}
              color="secondary"
              variant="outlined"
              sx={(themeArg) => verticalCollapseChevronIconButtonSx(themeArg, open, lightTextColor, darkTextColor)}
            >
              <NavCollapseChevronTrail miniMenuOpened={miniMenuOpened} open={open} arrowStyle={arrowStyle} />
            </IconButton>
          ) : (
            <Box component="span" sx={(themeArg) => ({ color: lightTextColor, ...themeArg.applyStyles('dark', { color: darkTextColor }) })}>
              <NavCollapseChevronTrail miniMenuOpened={miniMenuOpened} open={open} arrowStyle={arrowStyle} />
            </Box>
          ))}

        {!drawerOpen && (
          <PopperStyled open={miniMenuOpened} anchorEl={anchorEl} placement="right-start" sx={{ zIndex: 2001 }}>
            {({ TransitionProps }) => (
              <Transitions in={miniMenuOpened} {...TransitionProps}>
                <Paper sx={(themeArg) => collapseMiniPopperPaperSx(themeArg)}>
                  <ClickAwayListener onClickAway={handleClose}>
                    <SimpleBar sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: 'calc(100vh - 170px)' }}>{navCollapse}</SimpleBar>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </PopperStyled>
        )}
      </ListItemButton>
      <NavCollapseDrawerExpansion
        menu={menu}
        drawerOpen={drawerOpen}
        open={open}
        openCollapse={openCollapse}
        anchorElCollapse={anchorElCollapse}
        handleCloseCollapse={handleCloseCollapse}
        theme={theme}
        level={level}
        navCollapse={navCollapse}
      />
    </>
  );
}

function NavCollapseHorizontalRail(props: Readonly<{
  popperId: string | undefined;
  isSelected: boolean;
  handleHover: (event: MouseEvent<HTMLElement> | undefined) => void;
  handleClose: () => void;
  level: number;
  handlerIconLink: () => void;
  FlexBox: { display: string; justifyContent: string; alignItems: string; width: string };
  menuIcon: ReactNode;
  menu: NavItemType;
  lightTextColor: string;
  darkTextColor: string;
  miniMenuOpened: boolean;
  anchorEl: AnchorElState;
  navCollapse: ReactNode;
}>) {
  const {
    popperId,
    isSelected,
    handleHover,
    handleClose,
    level,
    handlerIconLink,
    FlexBox,
    menuIcon,
    menu,
    lightTextColor,
    darkTextColor,
    miniMenuOpened,
    anchorEl,
    navCollapse
  } = props;

  return (
    <ListItemButton
      id={`boundary-${popperId}`}
      selected={isSelected}
      onMouseEnter={handleHover}
      onMouseLeave={handleClose}
      onClick={handleHover}
      aria-describedby={popperId}
      disableTouchRipple
      className={anchorEl ? 'Mui-selected' : ''}
      sx={horizontalMiniCollapseListItemSx(level)}
    >
      <Box onClick={handlerIconLink} sx={FlexBox}>
        {menuIcon && (
          <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36, color: 'secondary.dark' }}>{menuIcon}</ListItemIcon>
        )}
        <ListItemText
          primary={
            <Typography
              variant="h6"
              sx={(theme) =>
                verticalCollapseTitleTypographySx(theme, Boolean(isSelected || anchorEl), lightTextColor, darkTextColor)
              }
            >
              <FormattedMessage id={menu.title} />
            </Typography>
          }
        />
        <Box
          component="span"
          sx={(theme) => ({
            color: lightTextColor,
            ...theme.applyStyles('dark', { color: darkTextColor })
          })}
        >
          {miniMenuOpened ? <ArrowRight2 size={12} /> : <ArrowDown2 size={12} />}
        </Box>
      </Box>

      {anchorEl && (
        <PopperStyled
          id={popperId}
          open={miniMenuOpened}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{ zIndex: 2001 }}
          modifiers={[{ name: 'offset', options: { offset: [-14, 0] } }]}
        >
          {({ TransitionProps }) => (
            <Transitions in={miniMenuOpened} {...TransitionProps}>
              <Paper sx={(theme) => collapseMiniPopperPaperSx(theme, true)}>
                <ClickAwayListener onClickAway={handleClose}>
                  <SimpleBar sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: 'calc(100vh - 170px)' }}>{navCollapse}</SimpleBar>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </PopperStyled>
      )}
    </ListItemButton>
  );
}

interface Props {
  menu: NavItemType;
  level: number;
  parentId: string;
  setSelectedItems: Dispatch<SetStateAction<string | undefined>>;
  selectedItems: string | undefined;
  setSelectedLevel: Dispatch<SetStateAction<number>>;
  selectedLevel: number;
}

export default function NavCollapse({ menu, level, parentId, setSelectedItems, selectedItems, setSelectedLevel, selectedLevel }: Readonly<Props>) {
  const theme = useTheme();
  const navigation = useNavigate();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuOrientation } = useConfig();

  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | null | undefined>(null);
  const [anchorEl, setAnchorEl] = useState<AnchorElState>(null);

  const [anchorElCollapse, setAnchorElCollapse] = useState<null | HTMLElement>(null);

  const openCollapse = Boolean(anchorElCollapse);
  const handleClickCollapse = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorElCollapse(event.currentTarget);
  };
  const handleCloseCollapse = () => {
    setAnchorElCollapse(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement> | undefined, isRedirect: boolean) => {
    runNavCollapsePrimaryClick({
      event,
      isRedirect,
      drawerOpen,
      open,
      selected,
      menu,
      level,
      navigation,
      setAnchorEl,
      setSelectedLevel,
      setOpen,
      setSelected,
      setSelectedItems
    });
  };

  const handlerIconLink = () => {
    runNavCollapseIconLink({ drawerOpen, menu, menuOrientation, navigation, setSelected });
  };

  const handleHover = (event: MouseEvent<HTMLElement> | undefined) => {
    setAnchorEl(event?.currentTarget);
  };

  const miniMenuOpened = Boolean(anchorEl);

  const handleMiniClose = () => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    runNavCollapseClose({ miniMenuOpened, menu, setOpen, setSelected, setAnchorEl });
  };

  useMemo(() => {
    applyNavCollapseSelectionSync({
      selected,
      selectedItems,
      level,
      selectedLevel,
      miniMenuOpened,
      drawerOpen,
      setOpen,
      setSelected
    });
  }, [selectedItems, level, selected, miniMenuOpened, drawerOpen, selectedLevel]);

  const { pathname } = useLocation();

  // menu collapse for sub-levels
  useMenuCollapse(menu, pathname, miniMenuOpened, setSelected, setOpen, setAnchorEl);

  useEffect(() => {
    if (menu.url === pathname) {
      setSelected(menu.id);
      setAnchorEl(null);
      setOpen(true);
    }
  }, [pathname, menu]);

  const navCollapse = menu.children?.map((item) =>
    mapNavMenuChild(item, {
      level,
      parentId,
      setSelectedItems,
      setSelectedLevel,
      selectedLevel,
      selectedItems
    })
  );

  const isSelected = selected === menu.id;
  const borderIcon = level === 1 ? <Copy variant="Bulk" size={drawerOpen ? 22 : 24} /> : false;
  const Icon = menu.icon!;
  const menuIcon = menu.icon ? <Icon variant="Bulk" size={drawerOpen ? 22 : 24} /> : borderIcon;
  const popperId = miniMenuOpened ? `collapse-pop-${menu.id}` : undefined;
  const FlexBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' };

  const selectedTextColor = isSelected || anchorEl ? 'primary.main' : null;
  const lightTextColor = selectedTextColor ?? 'secondary.main';
  const darkTextColor = selectedTextColor ?? 'secondary.400';

  const arrowStyle = { size: 12, style: { marginLeft: 1 } };

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <NavCollapseVerticalStack
          menu={menu}
          level={level}
          drawerOpen={drawerOpen}
          isSelected={isSelected}
          anchorEl={anchorEl}
          open={open}
          miniMenuOpened={miniMenuOpened}
          openCollapse={openCollapse}
          anchorElCollapse={anchorElCollapse}
          navCollapse={navCollapse}
          theme={theme}
          handleClick={handleClick}
          handleMiniClose={handleMiniClose}
          handleClickCollapse={handleClickCollapse}
          handleCloseCollapse={handleCloseCollapse}
          handlerIconLink={handlerIconLink}
          handleClose={handleClose}
          menuIcon={menuIcon}
          lightTextColor={lightTextColor}
          darkTextColor={darkTextColor}
          arrowStyle={arrowStyle}
        />
      ) : (
        <NavCollapseHorizontalRail
          popperId={popperId}
          isSelected={isSelected}
          handleHover={handleHover}
          handleClose={handleClose}
          level={level}
          handlerIconLink={handlerIconLink}
          FlexBox={FlexBox}
          menuIcon={menuIcon}
          menu={menu}
          lightTextColor={lightTextColor}
          darkTextColor={darkTextColor}
          miniMenuOpened={miniMenuOpened}
          anchorEl={anchorEl}
          navCollapse={navCollapse}
        />
      )}
    </>
  );
}
